// globalAttachmentViewerPatch.js
//
// Backend attachment URLs (`/webservice/attachments/file?path=...`) 401 with an
// "Authentication Required" page when the browser navigates to them as a top-level tab
// (Sec-Fetch-Dest: document) — see Backend/controllers/attachments.js isAllowedRequest().
// The same URL loaded inside an iframe (Sec-Fetch-Dest: iframe) is allowed, which is why
// pages using Helpers/FileDownloadUtils.js openFileInViewer() already preview correctly.
//
// A number of older pages copy-pasted that popup-viewer logic inline instead of calling the
// shared helper, and their own copies mis-detect the file type (their filename comes from the
// URL's last path segment, which for a proxy URL like `.../file?path=folder%2Fname.pdf` is just
// "file" — no extension). That sends them to the Google Docs / Office Online viewer fallback,
// which fetches the URL from Google's/Microsoft's own servers and fails ("No preview available")
// whenever the app isn't running on a publicly reachable host (e.g. a dev/staging environment).
//
// Rather than editing every page — the ones doing window.open(url, '_blank'),
// <a href={url} target="_blank">, and the ones with an inline popup + document.write copy —
// patch all three entry points globally, once, here.

import {
  openFileInViewer,
  buildFileViewerHtml,
  resolveAttachmentProxyPath,
  isPdfFile,
  isOfficeFile,
} from './FileDownloadUtils';

const ATTACHMENT_URL_MARKER = '/webservice/attachments/file';
const GVIEW_PREFIX = 'https://docs.google.com/gview?url=';
const OFFICE_PREFIX = 'https://view.officeapps.live.com/op/embed.aspx?src=';

function isAttachmentFileUrl(url) {
  return typeof url === 'string' && url.includes(ATTACHMENT_URL_MARKER);
}

function attachmentFileName(url) {
  return resolveAttachmentProxyPath(url)?.split('/').pop() || 'File';
}

function viewAttachmentUrl(url) {
  openFileInViewer(url, {
    isActualPdf: isPdfFile(url),
    isOfficeFile: isOfficeFile(url),
    fileName: attachmentFileName(url),
  });
}

function extractEncodedParam(html, prefix) {
  const idx = html.indexOf(prefix);
  if (idx === -1) return null;
  const rest = html.slice(idx + prefix.length);
  const end = rest.search(/["'&]/);
  return end === -1 ? rest : rest.slice(0, end);
}

// A popup's document.write() may embed one of our attachment URLs wrapped inside an
// external viewer URL (Google Docs / Office Online) — pull the real URL back out.
function findWrappedAttachmentUrl(html) {
  if (typeof html !== 'string') return null;
  const encoded = extractEncodedParam(html, GVIEW_PREFIX) || extractEncodedParam(html, OFFICE_PREFIX);
  if (!encoded) return null;
  try {
    const decoded = decodeURIComponent(encoded);
    return isAttachmentFileUrl(decoded) ? decoded : null;
  } catch (_) {
    return null;
  }
}

// Any window.open('', '_blank') (or similar blank popup) may be one of the inline
// popup-viewer copies about to document.write mis-detected viewer HTML. Patch that specific
// popup's document.write once so we can correct it in place before it renders.
function patchPopupDocumentWrite(popup) {
  try {
    const doc = popup.document;
    if (!doc || doc.__signwiseWritePatched) return;
    doc.__signwiseWritePatched = true;

    const originalWrite = doc.write.bind(doc);
    let rewritten = false;
    doc.write = function patchedPopupWrite(html) {
      if (!rewritten) {
        const realUrl = findWrappedAttachmentUrl(html);
        if (realUrl) {
          rewritten = true;
          html = buildFileViewerHtml(realUrl, {
            isActualPdf: isPdfFile(realUrl),
            isOfficeFile: isOfficeFile(realUrl),
            fileName: attachmentFileName(realUrl),
          });
        }
      }
      return originalWrite(html);
    };
  } catch (_) {
    /* cross-origin or already-closed popup — nothing we can do */
  }
}

function installGlobalAttachmentViewerPatch() {
  if (typeof window === 'undefined' || window.__signwiseAttachmentViewerPatchInstalled) return;
  window.__signwiseAttachmentViewerPatchInstalled = true;

  const originalWindowOpen = window.open.bind(window);
  window.open = function patchedWindowOpen(url, target, features) {
    // window.open(attachmentUrl, '_blank') → route through the iframe-based viewer instead
    // of a raw top-level navigation.
    if (isAttachmentFileUrl(url)) {
      viewAttachmentUrl(url);
      return null;
    }

    const popup = originalWindowOpen(url, target, features);
    // window.open('', '_blank') → likely a popup an inline copy of the viewer logic is about
    // to document.write into; watch for and correct a mis-detected viewer.
    if (popup && (!url || url === 'about:blank')) {
      patchPopupDocumentWrite(popup);
    }
    return popup;
  };

  // <a href={attachmentUrl} target="_blank"> → same fix for plain links. Anchors with a
  // `download` attribute are left alone — those are real download triggers, not previews.
  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented) return;
      const anchor = event.target?.closest?.('a[target="_blank"]');
      if (!anchor || anchor.hasAttribute('download')) return;
      const href = anchor.getAttribute('href');
      if (!isAttachmentFileUrl(href)) return;
      event.preventDefault();
      event.stopPropagation();
      viewAttachmentUrl(anchor.href);
    },
    true
  );
}

installGlobalAttachmentViewerPatch();
